import { getStorage } from '@/store/storage';

export const CONTINUATION_FRESH_MS = 5 * 60 * 1000;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const LIMIT = 12;
const owners = new Map();
const storageKey = (userId, mode) => `gab-discovery-continuations-v1:${userId}:${mode}`;
export const continuationKey = candidate => JSON.stringify([candidate.conversationId, candidate.updatedAt]);

export function validContinuation(suggestion, candidate) {
  return suggestion?.conversationId === candidate.conversationId && typeof suggestion.prompt === 'string'
    && suggestion.prompt.trim().length >= 12 && suggestion.prompt.length <= 240
    && Number.isFinite(Date.parse(suggestion.updatedAt))
    && Date.parse(suggestion.updatedAt) >= Date.parse(candidate.updatedAt);
}

export function readContinuation(owner, candidate) {
  const key = continuationKey(candidate), entry = owner.entries.get(key);
  if (!entry) return null;
  const age = Date.now() - entry.at;
  if (age < 0 || age >= (entry.suggestion ? MAX_AGE_MS : 30000)) {
    owner.entries.delete(key);
    return null;
  }
  return { ...entry, fresh: age < (entry.suggestion ? CONTINUATION_FRESH_MS : 30000) };
}

// Bounded account snapshots contain prompts and revision identifiers only.
// Membership always comes from the current eligible chat list.
export function continuationOwner(userId, session, mode) {
  const key = JSON.stringify([userId, session, mode]);
  if (owners.has(key)) return owners.get(key);
  const owner = { userId, mode, entries: new Map(), pending: new Map(), listeners: new Set(), disposed: false, write: Promise.resolve() };
  if (owners.size >= 8) {
    const oldest = owners.keys().next().value;
    owners.get(oldest).disposed = true;
    owners.delete(oldest);
  }
  owners.set(key, owner);
  const restore = raw => {
    if (owner.disposed) return;
    try {
      const rows = JSON.parse(raw || '[]');
      if (!Array.isArray(rows)) return;
      for (const row of rows.slice(-LIMIT)) {
        if (!Array.isArray(row) || row.length !== 2) continue;
        const [entryKey, entry] = row;
        const [conversationId, updatedAt] = JSON.parse(entryKey);
        if (!Number.isFinite(entry?.at) || !validContinuation(entry.suggestion, { conversationId, updatedAt })) continue;
        owner.entries.set(entryKey, entry);
        readContinuation(owner, { conversationId, updatedAt });
      }
    } catch { /* A missing or malformed local cache never blocks the page. */ }
    owner.listeners.forEach(notify => notify());
  };
  try {
    const raw = getStorage().getItem(storageKey(userId, mode));
    // Web storage is synchronous, so the first render can use its snapshot.
    if (raw && typeof raw.then === 'function') owner.ready = Promise.resolve(raw).then(restore).catch(() => {});
    else { restore(raw); owner.ready = Promise.resolve(); }
  } catch { owner.ready = Promise.resolve(); }
  return owner;
}

export function rememberContinuation(owner, candidate, suggestion) {
  if (owner.disposed) return;
  const key = continuationKey(candidate);
  owner.entries.delete(key);
  owner.entries.set(key, { suggestion: suggestion ? {
    conversationId: suggestion.conversationId, updatedAt: suggestion.updatedAt, prompt: suggestion.prompt.trim(),
  } : null, at: Date.now() });
  while (owner.entries.size > LIMIT) owner.entries.delete(owner.entries.keys().next().value);
  const rows = [...owner.entries].filter(([, entry]) => entry.suggestion && Date.now() - entry.at < MAX_AGE_MS);
  owner.write = owner.write.then(() => {
    if (!owner.disposed) return getStorage().setItem(storageKey(owner.userId, owner.mode), JSON.stringify(rows));
  }).catch(() => {});
  owner.listeners.forEach(notify => notify());
}

export function clearDiscoveryContinuationCache() {
  for (const owner of owners.values()) {
    owner.disposed = true;
    // Remove after any pending native write, so logout cannot resurrect text.
    owner.write = owner.write.then(() => Promise.all(['chat', 'work'].map(mode =>
      getStorage().removeItem(storageKey(owner.userId, mode))))).catch(() => {});
    owner.entries.clear();
  }
  owners.clear();
}
